import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toaster } from '@optiaxiom/react';
import { useQueryClient } from '@tanstack/react-query';

import { EVENT_NAME } from '@frontend/constants';
import { useNotificationTracking } from '@frontend/features/notifications/hooks';
import { getDeepLinkPath, getNotificationIntent } from '@frontend/features/notifications/utils';

import { useInstanceId } from './useInstanceId';
import { useNamespacedNavigate } from './useNamespacedNavigate';
import { useSocketChannel } from './useSocket';

// Module-level singleton updated by chat-frontend events. Intentionally
// outside useEffect so state persists across component remounts.
const messagingViewState = { tab: '', conversationId: '' };

document.addEventListener(EVENT_NAME.OPAL_IM_MODE_CHANGED, (e: Event) => {
  const detail = (e as CustomEvent).detail as { mode: string } | undefined;
  messagingViewState.tab = detail?.mode ?? '';
});

document.addEventListener(EVENT_NAME.OPAL_IM_CONVERSATION_CHANGED, (e: Event) => {
  const detail = (e as CustomEvent).detail as { conversationId: string } | undefined;
  messagingViewState.conversationId = detail?.conversationId ?? '';
});

type NotificationCreatedData = {
  notification_id: string;
  opal_instance_id: string;
  type: string;
  title: string;
  message: string;
  agent_id?: string;
  metadata?: {
    execution_id?: string;
    thread_id?: string;
    [key: string]: unknown;
  };
  created_at: string;
};

export function useNotificationEvents() {
  const { instanceId } = useInstanceId();
  const location = useLocation();
  const navigate = useNamespacedNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { clicked } = useNotificationTracking();

  const onNotificationCreated = useCallback(
    (data: unknown) => {
      const notification = data as NotificationCreatedData;

      // Ignore notifications for other instances
      if (notification.opal_instance_id !== instanceId) {
        return;
      }

      // Notifications may fold into an existing group, so the count can't be
      // optimistically bumped — invalidation refetches the reconciled count.
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Suppress toast if user is viewing the relevant thread
      if (notification.metadata?.thread_id) {
        const currentThreadId = searchParams.get('threadId');
        if (currentThreadId === notification.metadata.thread_id) {
          return;
        }
      }

      if (notification.type?.startsWith('messaging_')) {
        const notifConversationId = (notification.metadata as Record<string, unknown>)
          ?.conversation_id as string | undefined;
        if (
          messagingViewState.tab === 'messages' &&
          messagingViewState.conversationId &&
          messagingViewState.conversationId === notifConversationId
        ) {
          return;
        }
      }

      // Suppress toast if user is viewing the relevant execution
      if (notification.metadata?.execution_id) {
        const isOnExecutionPage = location.pathname.includes(
          `/executions/${notification.metadata.execution_id}`
        );
        if (isOnExecutionPage) {
          return;
        }
      }

      toaster.create(notification.title, {
        action: 'View',
        intent: getNotificationIntent(notification.type),
        onAction: () => {
          clicked(notification.type, 'toast');
          if (notification.type === 'workflow_execution_hitl_requested') {
            navigate('/notifications');
            return;
          }
          if (notification.type?.startsWith('messaging_')) {
            const meta = notification.metadata as Record<string, unknown> | undefined;
            document.dispatchEvent(
              new CustomEvent(EVENT_NAME.OPAL_IM_NOTIFICATION_CLICKED, {
                detail: {
                  conversationId: meta?.conversation_id,
                  messageId: meta?.message_id,
                  msgThreadId: meta?.thread_id,
                },
                bubbles: true,
              })
            );
          }
          navigate(getDeepLinkPath(notification));
        },
      });
    },
    [clicked, instanceId, location.pathname, navigate, queryClient, searchParams]
  );

  useSocketChannel('notification_created', onNotificationCreated);

  const onBadgeSeen = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'badge-count'] });
  }, [queryClient]);

  useSocketChannel('badge_seen', onBadgeSeen);
}
